import { refreshUserToken } from "../api/refreshUserToken";
import { NetworkError } from "../store/NetworkError";
import { ReauthRequiredError } from "../store/ReauthRequiredError";
import { useAuthStore } from "../store/useAuthStore";

/**
 * 業務 API 呼び出し用の fetch ラッパー (Cookie 認証版)。
 *
 * - `credentials: 'include'` で auth_access / auth_refresh Cookie を自動送信
 * - 401 で `/user-tokens/refresh` を single-flight 呼び出し、Cookie 更新後に元 request を 1 度だけリトライ
 * - refresh も 401 で失敗した場合は `ReauthRequiredError` throw
 * - ネットワーク失敗は `NetworkError` throw
 */
export async function fetchWithAuth(
    input: RequestInfo | URL,
    init: RequestInit = {}
): Promise<Response> {
    const initWithCredentials: RequestInit = { ...init, credentials: "include" };

    let res = await safeFetch(input, initWithCredentials);
    if (res.status !== 401) return res;

    const refreshOk = await tryRefresh();
    if (!refreshOk) {
        useAuthStore.getState().setReauthRequired();
        throw new ReauthRequiredError();
    }

    res = await safeFetch(input, initWithCredentials);
    if (res.status === 401) {
        useAuthStore.getState().setReauthRequired();
        throw new ReauthRequiredError();
    }
    return res;
}

async function safeFetch(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
    try {
        return await fetch(input, init);
    } catch (e) {
        throw new NetworkError(e instanceof Error ? e.message : "Network error");
    }
}

let inflightRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
    if (inflightRefresh) return inflightRefresh;
    inflightRefresh = (async () => {
        try {
            await refreshUserToken();
            return true;
        } catch (e) {
            if (e instanceof NetworkError) throw e;
            return false;
        } finally {
            inflightRefresh = null;
        }
    })();
    return inflightRefresh;
}
